import {
	apolloConfigEntryKind,
	employeeRangeBoundsOf,
	isApolloFilterCondition,
	LegacyApolloFilter,
	legacyApolloFilterOf,
} from "@signalist/shared-types";

import { IFilterWithValues } from "../../../components/FilterModal";
import { IFilterType } from "../../../constants/general-filter";
import { IConfig, IConfigGeneral } from "../interfaces/config-interface";

type StoredCondition = Partial<IConfigGeneral[string]> & { value?: unknown };
type FilterRow = IFilterWithValues[string];

/** A legacy array as the dropdown row the step renders. */
const dropdownRow = (legacy: LegacyApolloFilter, values: unknown[]): FilterRow => ({
	type: "dropdown",
	filterParam: legacy.filterParam,
	value: values.map((value) => ({ id: String(value), title: String(value) })),
});

/** A legacy employee range as the one between row the step renders and saves back. */
const employeeRangeRow = (
	legacy: LegacyApolloFilter,
	bounds: { from?: string; till?: string }
): FilterRow => ({
	type: "between",
	filterParam: legacy.filterParam,
	value: bounds,
});

/** A stored condition as a row; a legacy key's field is the one the API applies it to. */
const conditionRow = (condition: StoredCondition, legacy?: LegacyApolloFilter): FilterRow => ({
	type: (condition.type ?? legacy?.defaultType) as IFilterType,
	filterParam: (legacy?.filterParam ?? condition.filterParam) as string,
	value: condition.value as FilterRow["value"],
	...(condition.customColumnId !== undefined && { customColumnId: condition.customColumnId }),
});

/** The row name and row of one legacy entry. */
function legacyRow(legacy: LegacyApolloFilter, stored: unknown): FilterRow {
	if (Array.isArray(stored) && legacy.valueSource !== "employeeRange") {
		return dropdownRow(legacy, stored);
	}

	const bounds = legacy.valueSource === "employeeRange" && employeeRangeBoundsOf(stored);

	if (bounds) {
		return employeeRangeRow(legacy, bounds);
	}

	// A range stored as a condition keeps the field it names; every other legacy key takes the
	// table's, since the API overrides it by name.
	const fieldFromTable = !(
		legacy.valueSource === "employeeRange" && isApolloFilterCondition(stored)
	);

	return conditionRow(stored as StoredCondition, fieldFromTable ? legacy : undefined);
}

/**
 * Maps a saved Apollo SOURCE config into the filter rows the Filter step renders (PREC-4622).
 * Which entries are filters is the shared `apolloConfigEntryKind`, the decision the API's filter
 * run and config merge use: settings other steps own never become rows (they were saved back as
 * `{}` over the setting), and a filter nothing can be read from is shown apart by the step
 * (`UnreadableFilterRows`), never as a row. A legacy key is read through LEGACY_APOLLO_FILTERS,
 * which names the row and the field. Pure transform extracted from ListEditFilter so the page
 * stays under the file-length cap.
 */
export const mapApolloConfigToFilter = (
	config: IConfig,
	currentFilter: IFilterWithValues | undefined
): IFilterWithValues | undefined => {
	let newFilter = currentFilter;

	for (const [key, stored] of Object.entries(config ?? {})) {
		if (apolloConfigEntryKind(key, stored) !== "filter") {
			continue;
		}

		const legacy = legacyApolloFilterOf(key);

		newFilter = {
			...newFilter,
			[legacy?.filterName ?? key]: legacy
				? legacyRow(legacy, stored)
				: conditionRow(stored as StoredCondition),
		};
	}

	return newFilter;
};
